import { alpha, Box, Stack, Typography, useTheme } from "@mui/material";
import Badge from "components/elements/Badge";
import CircularProgressWithLabel from "components/elements/CircularProgressWithLabel";
import { Check, ChevronRight } from "components/icons";
import { fontWeight } from "config/Theme/options";
import { useMUIThemeModeContext } from "context/MUIThemeModeContext";
import { useUserDataContext } from "context/UserDataContext";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { WeeklyQuizStatus } from "types/enums";
import { renderNumber } from "utils/formatter";

import Atlas from "./Atlas";

export const ChallengeStatusBadge: React.FC<{ status: WeeklyQuizStatus, width?: string | number; }> = ({ status, width }) => {
  const { t } = useTranslation();

  const getStatusColor = (status: WeeklyQuizStatus) => {
    switch (status) {
      case WeeklyQuizStatus.WAITING:
      case WeeklyQuizStatus.OPEN:
        return "warning";
      case WeeklyQuizStatus.DONE:
        return "success";
      case WeeklyQuizStatus.FROZEN_1:
      case WeeklyQuizStatus.FROZEN_2:
        return "info";
      case WeeklyQuizStatus.FROZEN_3:
        return "error";
      default:
        return "info";
    }
  };

  return (
    <Badge color={getStatusColor(status)} withTextTransform={false} width={width}>
      {status === WeeklyQuizStatus.DONE && <Check fontSize="1rem" />}
      <Typography
        fontWeight={fontWeight.medium}
        fontSize={"0.8125rem"}
        sx={{ py: 0.5, pl: status === WeeklyQuizStatus.DONE ? 0.5 : 0 }}>
        {t(`bitcoinerChallenge.status.${status}`)}
      </Typography>
    </Badge>
  );
};

const ChallengeStatusCard = () => {
  const { t } = useTranslation();
  const { colors } = useMUIThemeModeContext();
  const theme = useTheme();
  const { bitcoinerChallenge } = useUserDataContext();
  const navigate = useNavigate();

  const getProgressPercentage = (currentXP: number, maxXP: number) => {
    if (maxXP === 0) return 0;
    return Math.round((100 * currentXP) / maxXP);
  };

  if (!bitcoinerChallenge) {
    return null;
  }

  return (
    <Box
      p={1.5}
      mb={0.5}
      mx={1}
      borderRadius={4}
      bgcolor={(theme) => theme.palette.background.paper}
      border={`1px solid ${colors.gray200}`}
      sx={{ cursor: "pointer" }}
      onClick={() => navigate("/vyzva-bitcoinera")}>
      <Stack direction={"row"} spacing={1.5} alignItems={"center"}>
        <CircularProgressWithLabel
          value={getProgressPercentage(bitcoinerChallenge.xpTotal, bitcoinerChallenge.xpForNextLevel)}
          size={53}
          label={
            <Stack textAlign={"center"}>
              <Typography fontSize={"0.875rem"} color={"primary.main"} fontWeight={fontWeight.semiBold}>
                {renderNumber(bitcoinerChallenge.xpTotal)}
              </Typography>
              <Typography fontSize={"0.625rem"} color={"primary.main"} fontWeight={fontWeight.semiBold}>
                {t("bottomNavigation.challenge.xp")}
              </Typography>
            </Stack>
          }
        />
        <Stack direction={"column"} spacing={0.5} width={"100%"}>
          <Stack direction={"row"} spacing={0.5} alignItems={"center"}>
            <Typography fontWeight={fontWeight.semiBold} fontSize={"0.875rem"}>
              {t("bitcoinerChallenge.title")}
            </Typography>
            <Box borderRadius={1} px={0.5} border={`1px solid ${alpha(colors.black, 0.05)}`}>
              <Typography fontWeight={fontWeight.medium} fontSize={"0.6875rem"} color={colors.gray400}>
                {t("bitcoinerChallenge.level", { level: bitcoinerChallenge.levelNumber })}
              </Typography>
            </Box>
          </Stack>
          <Stack direction={"row"} justifyContent={"space-between"} alignItems={"center"}>
            <ChallengeStatusBadge status={bitcoinerChallenge.weeklyQuizStatus} />
            <ChevronRight style={{ color: theme.palette.primary.main }} />
          </Stack>
        </Stack>
      </Stack>
      <Atlas />
    </Box>
  );
};

export default ChallengeStatusCard;
